/* eslint-disable no-param-reassign */

import { getBlurhashFromUrl, useMediaLazyLoad } from "@/shared";
import { isImageSource } from "@/shared/utils/is-image-source";
import Blurhash from "@components/blurhash";
import type { StaticEntityType } from "@config";
import { useViewContext } from "@editor-providers";
import { LinkPopUp } from "@library/components/LinkPopUp";
import { PdfLibVideoWrapper } from "@library/components/pdf";
import { getTabByAnchorInside } from "@library/shared";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useWindowSize } from "usehooks-ts";
import {
  GridColorStyled,
  GridItemStyled,
  GridStyled,
  WrapperStyled,
} from "../styles/Grid.styled";
import "../styles/index.scss";
import type {
  Data,
  EditorDataProps,
  EditorSlotsProps,
  GridProps,
} from "../types";
import { getGridMobileCols } from "../utils/getGridMobileCols";

type Link = {
  activeType?: string;
  url?: string;
  analytics?: string;
  popup?: boolean;
  anchor?: string;
};

type Item = {
  id: string;
  value: string;
  classification?: string;
  variable?: string;
  alt?: string;
  overlay?: boolean;
  overlayOpacity?: number;
  overlayColor?: string;
  masonaryHeight?: number;
} & Link;

type MasonryItem = {
  id: string;
  items: Item[];
};

const getGridRowsInfo = ({
  windowSize,
  perLine,
  gridType,
  totalItems,
  mobilePerLine,
}: {
  windowSize: number;
  gridType: string;
  totalItems: number;
  perLine: string;
  mobilePerLine?: string | number;
}) => {
  let cols = 1;
  let rows = 0;

  if (windowSize <= 768) {
    // Mobile and tablet follow the mobile setting (auto, mobile custom, or
    // legacy fallback). Same behaviour in regular combos and full screen.
    cols = getGridMobileCols({ perLine, mobileSetsPerLine: mobilePerLine });
  } else {
    if (perLine !== "auto") cols = Number(perLine);
    else if (totalItems <= 6) cols = totalItems;
    else cols = 6;
  }

  let lastRowFirstItemIndex = 0;
  if (gridType === "grid-8") {
    let remainingItems = totalItems;
    let lastAmt = 0;

    while (remainingItems > 0) {
      lastAmt = remainingItems;
      if (rows % 2 === 0) {
        remainingItems -= cols;
      } else {
        remainingItems -= Math.max(1, cols - 1);
      }
      rows++;
    }
    lastRowFirstItemIndex = Math.max(0, totalItems - lastAmt);
  } else rows = Math.ceil(totalItems / cols);

  if (Number.isNaN(cols) || cols === 0) cols = 1;
  if (Number.isNaN(rows) || rows === 0) rows = 1;

  return { cols, rows, lastRowFirstItemIndex };
};

export const GridLibrary = ({
  entity,
  editorSlots,
  editorData,
}: {
  entity: StaticEntityType;
  editorSlots?: EditorSlotsProps;
  editorData?: EditorDataProps;
}) => {
  const [linkPopUp, setLinkPopUp] = useState<string | undefined>("");
  const { view } = useViewContext();
  const { setObserverRef, isVisible } = useMediaLazyLoad();
  const { settings } = entity as StaticEntityType<GridProps>;
  const [gridContainerElWidth, setGridContainerElWidth] =
    useState<any>(undefined);
  const { width: windowSize } = useWindowSize();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});

  const width = editorData?.width || settings.width;
  const height = editorData?.height || settings.height;

  const [data, setData] = useState<Data[]>([]);
  const isPdf = view === "pdf";

  const { cols, rows, lastRowFirstItemIndex } = getGridRowsInfo({
    windowSize,
    totalItems: data.length,
    gridType: settings.gridType,
    perLine: settings.setsPerLine,
    mobilePerLine: settings.mobileSetsPerLine,
  });

  const handleOnClick = (item: Item) => {
    if (editorData) return;
    if (item.activeType === "hyperlink" && item.popup && item.url) {
      const url = item.url.startsWith("http")
        ? item.url
        : `https://${item.url}`;
      setLinkPopUp(url);
      return;
    }

    if (item.activeType === "anchor") {
      handleAnchorClick(item);
    }
  };

  const handleAnchorClick = (item: Item) => {
    if (!item.anchor) return;

    const anchorTargetElement = document.querySelector(
      '[data-anchor-target="' + item.anchor + '"]',
    );

    if (anchorTargetElement) {
      const tab = getTabByAnchorInside(anchorTargetElement);
      if (tab) {
        tab.click();
        setTimeout(() => {
          const tabsCombo = tab.closest('[data-combo-name="Tabs"]');
          (tabsCombo instanceof HTMLElement
            ? tabsCombo
            : anchorTargetElement
          ).scrollIntoView();
        });
        return;
      }

      const timelineItem = anchorTargetElement.closest(".timeline-item");
      if (timelineItem instanceof HTMLElement) {
        const timelineCombo = timelineItem.closest(
          '.timeline-combo[data-timeline-type="horizontal"]',
        );
        if (timelineCombo instanceof HTMLElement) {
          timelineCombo.scrollIntoView();
          timelineItem.click();
        }
        return;
      }

      anchorTargetElement.scrollIntoView();
    }
  };

  const handleGridScalingForFullScreen = useCallback(
    (scaleOnMobile = false) => {
      if (window.innerWidth <= 768 && !scaleOnMobile) {
        setGridContainerElWidth(undefined);
        return;
      }

      const container = containerRef.current as HTMLElement;
      if (!container) return;

      const grid = container.querySelector(".grid-container") as HTMLElement;
      if (!(grid instanceof HTMLElement)) return;
      const item = container.querySelector(".item") as HTMLElement;
      if (!(item instanceof HTMLElement)) return;
      const gap = Number.parseFloat(settings.space);

      grid.style.width = "100%";
      const itemRect = item.getBoundingClientRect();

      const numItems = rows;
      let containerHeight = container.clientHeight;
      let gapPerItem = 0;

      if (settings.gridType === "grid-8") {
        if (cols > 1) {
          const mb = getComputedStyle(item).getPropertyValue("margin-bottom");
          const mbNumber = Number.parseFloat(mb.replace(/px/g, ""));

          if (!container.closest(".thumbnailWrapper")) {
            itemRect.height += mbNumber - mbNumber / rows;
            // itemRect.height = Math.abs(itemRect.height);
          }
          gapPerItem = gap * 0.8;
        } else {
          containerHeight -= gap * 0.5;
          gapPerItem = gap * 1.5;
        }
      } else {
        containerHeight -= gap;
        gapPerItem = gap;
      }

      const heightWeHavePerItemWithMargins = containerHeight / numItems;
      const heightWeHavePerItem = heightWeHavePerItemWithMargins - gapPerItem;

      const itemAp = itemRect.width / itemRect.height;
      const itemWidth = heightWeHavePerItem * itemAp;

      const newWidth = (itemWidth + gap) * cols + "px";
      grid.style.width = newWidth;
      setGridContainerElWidth(newWidth);
    },
    [settings.gridType, settings.space, cols, rows],
  );

  useEffect(() => {
    if (
      !containerRef.current ||
      ["grid-6", "grid-7", "grid-5"].includes(settings.gridType)
    ) {
      setGridContainerElWidth(undefined);
      return;
    }
    const container = containerRef.current as HTMLElement;

    const fullScreenCombo = container.closest(
      "div.combo-full-screen-container",
    );
    const narratorCombo = container.closest("div.combo-narrator");
    if (fullScreenCombo) {
      const isInMediaGroup = container.closest("div.group.pos-1");
      if (
        fullScreenCombo.getAttribute("data-min-height") !== "screenFit" ||
        !isInMediaGroup
      ) {
        setGridContainerElWidth(undefined);
        return;
      }
    } else if (narratorCombo) {
      const isInMediaGroup = container.closest(".combo-narrator__media");
      if (!isInMediaGroup) {
        setGridContainerElWidth(undefined);
        return;
      }
    } else {
      setGridContainerElWidth(undefined);
      return;
    }

    const handleOnResize = () => {
      requestAnimationFrame(() => {
        handleGridScalingForFullScreen(!!narratorCombo);
      });
    };

    const resizeObserver = new ResizeObserver(handleOnResize);
    resizeObserver.observe(containerRef.current);

    handleGridScalingForFullScreen(!!narratorCombo);
    return () => {
      resizeObserver.disconnect();
    };
  }, [
    settings.gridType,
    settings.space,
    cols,
    rows,
    settings.radius,
    handleGridScalingForFullScreen,
  ]);

  useEffect(() => {
    const mapped = settings.data.every((item) => typeof item === "string")
      ? settings.data.map((item) => ({
          value: item,
          id: Math.random().toString(),
        }))
      : [...settings.data];
    setData(() => [...(mapped as Data[])]);
    if (videoRef && videoRef.current) {
      videoRef.current.load();
    }
  }, [settings.data]);

  const getElement = ({
    value,
    variable,
    alt,
  }: {
    value: string;
    variable?: string;
    alt?: string;
  }) => {
    if (variable && !variable.includes("{{")) {
      value = variable;
      if (/domain|theme|url/.test(variable)) {
        try {
          const parsedSrc = variable.replace(/&quot;/g, '"');
          const temp = JSON.parse(parsedSrc).url;
          if (temp) value = temp;
        } catch {}
      }
    }

    if (/\.(mp4|webm)(\?.*)?$/i.test(value)) {
      let url = "";
      if (isPdf) url = `${value}#t=0.1`;
      else url = isVisible ? value : "";
      return (
        <PdfLibVideoWrapper href={value}>
          {url && (
            <video
              autoPlay={!isPdf}
              muted
              playsInline={!isPdf}
              loop={!isPdf}
              key={url}
              preload={isPdf ? "auto" : undefined}
              {...(alt ? { "aria-label": alt } : {})}
            >
              <Source url={url} />
            </video>
          )}
        </PdfLibVideoWrapper>
      );
    }
    if (isImageSource(value)) {
      const hash = /^https?:\/\//i.test(value)
        ? getBlurhashFromUrl(value)
        : null;
      const isLoaded = loadedImages[value];
      return (
        <>
          <img
            src={value}
            alt={alt || ""}
            role="presentation"
            className="tw-relative tw-z-1"
            onLoad={() =>
              setLoadedImages((prev) => ({ ...prev, [value]: true }))
            }
          />
          {hash && !isLoaded && <Blurhash blurhash={hash} />}
        </>
      );
    }
    return <GridColorStyled $bgColor={value} className="grid-item-color" />;
  };

  const items: Array<MasonryItem | Item> = useMemo(() => {
    if (settings.gridType === "grid-5") {
      const tmp: MasonryItem[] = [];

      for (let i = 0; i < data.length; i++) {
        const columnIndex = i % cols;

        if (!tmp[columnIndex]) {
          tmp.push({
            id: Math.random().toString(),
            items: [],
          });
        }

        tmp[columnIndex].items.push({
          id: Math.random().toString(),
          value: data[i].value,
          variable: data[i].variable,
          overlay: data[i].overlay,
          overlayOpacity: data[i].overlayOpacity,
          overlayColor: data[i].overlayColor,
          activeType: data[i].activeType,
          url: data[i].url,
          analytics: data[i].analytics,
          popup: data[i].popup,
          anchor: data[i].anchor,
        });
      }

      return tmp;
    }

    return data;
  }, [data, cols, settings.gridType]);

  return (
    <WrapperStyled
      $align={settings.horizontalAlignment}
      $type={settings.gridType}
      onDoubleClick={editorData?.setActiveEditDialog}
      ref={(r) => {
        containerRef.current = r;
        if (editorData?.containerRef) editorData.containerRef.current = r;
      }}
      className="Grid-Wrapper"
    >
      <div className="wrapper">
        {editorSlots?.resizeSlot}
        <GridStyled
          $gap={settings.space}
          $radius={settings.radius}
          $setsPerLine={cols}
          $length={data.length}
          $rows={rows}
          style={
            {
              "--entity-size": `${width}%`,
              "--entity-height": `${height}px`,
            } as CSSProperties
          }
          $cols={cols}
          className={`${settings.gridType} grid-grid`}
          data-cols={cols}
          {...EDITOR_ATTRIBUTES({ isEditor: !!editorData })}
        >
          <div className="grid-inner" ref={setObserverRef}>
            <div
              className="grid-container"
              style={{
                width: gridContainerElWidth,
              }}
            >
              {settings.gridType === "grid-5"
                ? (items as MasonryItem[]).map((c, ci) => (
                    <div key={ci} className="grid__masonry_row">
                      {c.items.map((item, ii) => {
                        const hasLink = ["anchor", "hyperlink"].includes(
                          item.activeType || "",
                        );
                        return (
                          <GridItemStyled
                            $hasLink={hasLink}
                            $overlayColor={item.overlayColor || ""}
                            $overlayOpacity={item.overlayOpacity || 0}
                            $overlay={item.overlay || false}
                            className="item"
                            key={item.id || ii}
                            {...LINK_ATTRIBUTES({
                              item,
                              isLink: !editorData && hasLink,
                            })}
                            style={{
                              flex: (() => {
                                if (ci % 3 === 0) {
                                  return ii % 2 === 0 ? 3 : 2;
                                } else if (ci % 3 === 1) {
                                  return ii % 2 === 0 ? 2 : 5;
                                } else if (ci % 3 === 2) {
                                  return ii % 2 === 0 ? 2 : 3;
                                }
                                return 1;
                              })(),
                            }}
                            onClick={() => handleOnClick(item)}
                          >
                            {getElement(item)}
                            {item.variable && editorSlots?.variablesLabelSlot}
                          </GridItemStyled>
                        );
                      })}
                    </div>
                  ))
                : (items as Item[]).map((item, ii) => {
                    const hasLink = ["anchor", "hyperlink"].includes(
                      item.activeType || "",
                    );
                    return (
                      <GridItemStyled
                        $hasLink={hasLink}
                        $overlayColor={item.overlayColor || ""}
                        $overlayOpacity={item.overlayOpacity || 0}
                        $overlay={item.overlay || false}
                        {...LINK_ATTRIBUTES({
                          item,
                          isLink: !editorData && hasLink,
                        })}
                        className="item"
                        key={ii}
                        style={
                          settings.gridType === "grid-8" &&
                          gridContainerElWidth != null
                            ? {
                                marginTop: ii < cols ? 0 : undefined,
                                marginBottom:
                                  ii >= lastRowFirstItemIndex
                                    ? cols > 1
                                      ? "calc(var(--m) * 0.1)"
                                      : 0
                                    : undefined,
                              }
                            : undefined
                        }
                        onClick={() => handleOnClick(item)}
                      >
                        {getElement(item)}
                        {item.variable && editorSlots?.variablesLabelSlot}
                      </GridItemStyled>
                    );
                  })}
            </div>
          </div>
        </GridStyled>
      </div>

      <svg>
        <defs>
          <clipPath
            id="grid-rounded-hexagon-clipath"
            clipPathUnits="objectBoundingBox"
          >
            <path d="M0.407,0.023 C0.465,-0.008,0.535,-0.008,0.593,0.023 L0.907,0.188 C0.965,0.218,1,0.274,1,0.335 V0.665 C1,0.726,0.965,0.782,0.907,0.812 L0.593,0.977 C0.535,1,0.465,1,0.407,0.977 L0.093,0.812 C0.035,0.782,0,0.726,0,0.665 V0.335 C0,0.274,0.035,0.218,0.093,0.188 L0.407,0.023"></path>
          </clipPath>
        </defs>
      </svg>
      {!!linkPopUp && (
        <LinkPopUp src={linkPopUp} closePopUp={() => setLinkPopUp("")} />
      )}
    </WrapperStyled>
  );
};

const Source = ({ url }: { url: string }) => {
  if (url.endsWith(".webm")) {
    const mp4Url = url.replace(/\.webm$/, ".mp4");
    return (
      <>
        <source src={`${url}#t=0.1`} type="video/webm" />
        <source src={`${mp4Url}#t=0.1`} type="video/mp4" />
      </>
    );
  }
  return <source src={`${url}#t=0.1`} type="video/mp4" />;
};

const EDITOR_ATTRIBUTES = ({ isEditor }: { isEditor: boolean }) => {
  const payload: Record<string, string> = {
    "data-dnd-trigger": "true",
  };
  return isEditor ? payload : {};
};

const LINK_ATTRIBUTES = ({ item, isLink }: { item: Item; isLink: boolean }) => {
  if (!isLink || !item.url || item.popup) return {};
  return {
    href: item.url.startsWith("http") ? item.url : `https://${item.url}`,
    target: "_blank",
    as: "a",
  };
};
